/**
 * ai-animation.js — AI 特效库（自研滑块引擎）
 *
 * 从零实现的轻量级滑块引擎，现代 JS（const/let/箭头函数）。
 * 核心能力：snap 吸附、touch/drag、loop、响应式断点、事件系统、插件扩展。
 * 零外部依赖，任何 Shopify 主题引入即用。
 *
 * 通过 <ai-slider> Web Component 声明式使用。
 */
(function () {
  'use strict';

  // ──────────────────────────────────────────────
  // 工具函数
  // ──────────────────────────────────────────────

  const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
  const sign = (n) => (n > 0 ? 1 : 0) - (n < 0 ? 1 : 0) || +n;
  const round6 = (n) => Math.round(n * 1e6) / 1e6;
  const now = () => Date.now();

  const getSize = (el) => {
    const r = el.getBoundingClientRect();
    const roundFloor = (measured, offset) => {
      const f = Math.floor(measured);
      return f === offset || f + 1 === offset ? measured : offset;
    };
    return {
      width: roundFloor(r.width, el.offsetWidth),
      height: roundFloor(r.height, el.offsetHeight),
    };
  };

  const toArray = (v) => Array.prototype.slice.call(v);

  const queryElements = (input, ctx = document) => {
    if (typeof input === 'function') input = input(ctx);
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') return toArray(ctx.querySelectorAll(input));
    if (input instanceof HTMLElement) return [input];
    if (input instanceof NodeList) return toArray(input);
    return [];
  };

  const getProp = (obj, key, fallback, callIfFn = false) => {
    const v = obj && obj[key];
    if (v == null) return fallback;
    return callIfFn && typeof v === 'function' ? v() : v;
  };

  // ──────────────────────────────────────────────
  // 事件绑定器
  // ──────────────────────────────────────────────

  const createEventBinder = () => {
    const list = [];
    return {
      add(el, event, fn, opts) {
        el.addEventListener(event, fn, opts);
        list.push([el, event, fn, opts]);
      },
      purge() {
        list.forEach(([el, event, fn, opts]) => el.removeEventListener(event, fn, opts));
        list.length = 0;
      },
    };
  };

  // ──────────────────────────────────────────────
  // Animator — 关键帧动画引擎
  // ──────────────────────────────────────────────

  const createAnimator = (slider) => {
    let active = false;
    let targetIdx = null;
    let segments = null;
    let segIdx = 0;
    let totalDur = 0;
    let startTime = null;
    let rafId = null;

    const tick = (ts) => {
      if (!startTime) startTime = ts;
      let elapsed = ts - startTime;
      if (elapsed > totalDur) elapsed = totalDur;

      const seg = segments[segIdx];
      // seg = [basePos, distance, startTime, endTime, fullDuration, easingFn]
      if (seg[3] < elapsed) {
        segIdx++;
        return tick(ts);
      }

      const segDur = seg[4];
      const progress = segDur === 0 ? 1 : (elapsed - seg[2]) / segDur;
      const delta = seg[1] * seg[5](progress);
      if (delta) slider.track.to(seg[0] + delta);

      if (elapsed < totalDur) {
        rafId = requestAnimationFrame(tick);
      } else {
        startTime = null;
        active = false;
        targetIdx = null;
        slider.emit('animationEnded');
      }
    };

    const stop = () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (startTime) slider.emit('animationStopped');
      active = false;
      targetIdx = null;
      startTime = null;
    };

    return {
      get active() { return active; },
      get targetIdx() { return targetIdx; },

      start(keyframes) {
        stop();
        if (!slider.track.details) return;

        let pos = slider.track.details.position;
        let accDist = 0;
        segIdx = 0;
        totalDur = 0;

        segments = keyframes.map((kf) => {
          const base = pos;
          const earlyExit = kf.earlyExit != null ? kf.earlyExit : kf.duration;
          const dist = kf.distance * kf.easing(earlyExit / kf.duration) || 0;
          pos += dist;
          const segStart = totalDur;
          totalDur += earlyExit;
          accDist += dist;
          return [base, kf.distance, segStart, totalDur, kf.duration, kf.easing];
        });

        targetIdx = slider.track.distToIdx(accDist);
        active = true;
        rafId = requestAnimationFrame(tick);
        slider.emit('animationStarted');
      },

      stop,
    };
  };

  // ──────────────────────────────────────────────
  // Track — 位置计算与 slide 管理
  // ──────────────────────────────────────────────

  const createTrack = (slider) => {
    let opts, trackConfig, slideCount, totalLen, absLen;
    let origins, lastIdx;
    let minBound, maxBound, loopMin, loopMax, posMin, posMax;
    let position = 0;
    let history = [];
    let lastEmittedAbs = null;

    // ---- 内部工具 ----

    const isLoop = () => !!opts.loop;
    const absToRel = (n) => ((n % slideCount) + slideCount) % slideCount;
    const isValid = (n) => clampIdx(n) === n;
    const clampIdx = (n) => clamp(n, minBound, maxBound);

    const resolveIdx = (pos) => {
      const modLen = round6((pos % totalLen + totalLen) % totalLen);
      const dir = sign(pos);
      let loops = Math.floor(Math.abs(round6(pos / totalLen)));

      // 找最近的 origin
      const closest = origins.slice(0, slideCount).reduce((best, o) =>
        Math.abs(o - modLen) < Math.abs(best - modLen) ? o : best
      );
      let origin = origins.indexOf(closest);
      let rel = origin;

      if (dir < 0) loops++;
      if (origin === slideCount) {
        rel = 0;
        loops += dir > 0 ? 1 : -1;
      }

      return { abs: rel + loops * slideCount * dir, origin, rel };
    };

    const calcDist = (targetAbs, from) => {
      if (from == null) from = round6(position);
      if (!isValid(targetAbs) || targetAbs == null) return null;

      targetAbs = Math.round(targetAbs);
      const info = resolveIdx(from);
      const curMod = (from % totalLen + totalLen) % totalLen;
      const baseOrigin = origins[info.origin];
      const loopOffset = Math.floor((targetAbs - (info.abs - info.rel)) / slideCount) * totalLen;

      return round6(
        baseOrigin - curMod - baseOrigin +
        origins[absToRel(targetAbs)] +
        loopOffset +
        (info.origin === slideCount ? totalLen : 0)
      );
    };

    // ---- 公开方法 ----

    const add = (delta) => {
      history.push({ distance: delta, timestamp: now() });
      if (history.length > 6) history = history.slice(-6);
      position = round6(position + delta);

      const details = getDetails();
      slider.emit('detailsChanged');
      if (details) {
        const absIdx = details.abs;
        if (absIdx !== lastEmittedAbs) {
          const hadPrev = lastEmittedAbs !== null;
          lastEmittedAbs = absIdx;
          if (hadPrev) slider.emit('slideChanged');
        }
      }
    };

    const distToIdx = (dist) => {
      const info = resolveIdx(position + dist);
      return isValid(info.abs) ? info.abs : null;
    };

    const idxToDist = (target, useLoop = false, from) => {
      if (useLoop || !isLoop()) return calcDist(target, from);
      if (!isValid(target)) return null;

      const info = resolveIdx(from != null ? from : position);
      const diff = target - info.rel;
      const candidate = info.abs + diff;

      const d = calcDist(candidate);
      const alt = calcDist(candidate - slideCount * sign(diff));

      if (alt !== null && (d === null || Math.abs(alt) < Math.abs(d))) return alt;
      return d != null ? round6(d) : null;
    };

    const getDetails = () => {
      if (!slideCount) return null;

      const looped = isLoop();
      const modPos = looped ? ((position % totalLen) + totalLen) % totalLen : position;
      const rel0Offset = (looped ? position % totalLen : position) - trackConfig[0][2];
      let runningOffset = -(rel0Offset < 0 && looped ? totalLen - Math.abs(rel0Offset) : rel0Offset);
      let acc = 0;

      const info = resolveIdx(position);
      let abs = info.abs;
      let rel = info.rel;
      const origin0 = trackConfig[rel][2];

      const slides = trackConfig.map((cfg, i) => {
        let dist = runningOffset + acc;
        if ((dist < -cfg[0] || dist > 1) && looped) {
          dist += (Math.abs(dist) > totalLen - 1 ? totalLen : 0) * sign(-dist);
        }

        const diff = i - rel;
        const dir = sign(diff);
        let h = diff + abs;

        if (looped) {
          if (dir === -1 && dist > origin0) h += slideCount;
          if (dir === 1 && dist < origin0) h -= slideCount;
          if (loopMin !== null && h < loopMin) dist += totalLen;
          if (loopMax !== null && h > loopMax) dist -= totalLen;
        }

        const end = dist + cfg[0] + cfg[1];
        const portion = Math.max(
          dist >= 0 && end <= 1
            ? 1
            : end < 0 || dist > 1
              ? 0
              : dist < 0
                ? Math.min(1, (cfg[0] + dist) / cfg[0])
                : (1 - dist) / cfg[0],
          0
        );

        acc += cfg[0] + cfg[1];
        return { abs: h, distance: dist, portion, size: cfg[0] };
      });

      abs = clampIdx(abs);
      rel = absToRel(abs);

      const details = {
        abs, rel, length: absLen,
        min: posMin, max: posMax,
        minIdx: minBound, maxIdx: maxBound,
        position,
        progress: looped ? modPos / totalLen : (absLen ? position / absLen : 0),
        slides, slidesLength: totalLen,
      };

      return details;
    };

    const velocity = () => {
      const t = now();
      const result = history.reduce((acc, h) => {
        if (t - h.timestamp > 200) return acc;
        if (sign(h.distance) !== sign(acc.distance) && acc.distance) {
          return { distance: 0, lastTimestamp: 0, time: 0 };
        }
        if (acc.time) acc.distance += h.distance;
        if (acc.lastTimestamp) acc.time += h.timestamp - acc.lastTimestamp;
        acc.lastTimestamp = h.timestamp;
        return acc;
      }, { distance: 0, lastTimestamp: 0, time: 0 });
      return result.distance / result.time || 0;
    };

    const init = (initial) => {
      // 解析 trackConfig
      opts = slider.options;
      trackConfig = (opts.trackConfig || []).map((c) => [
        getProp(c, 'size', 1),
        getProp(c, 'spacing', 0),
        getProp(c, 'origin', 0),
      ]);
      slideCount = trackConfig.length;

      if (slideCount) {
        totalLen = round6(trackConfig.reduce((s, c) => s + c[0] + c[1], 0));
        const last = slideCount - 1;
        absLen = round6(totalLen + trackConfig[0][2] - trackConfig[last][0] - trackConfig[last][2] - trackConfig[last][1]);

        lastIdx = 0;
        origins = trackConfig.reduce((arr, c) => {
          if (!arr) return [0];
          const prev = trackConfig[arr.length - 1];
          let val = arr[arr.length - 1] + (prev[0] + prev[2]) + prev[1] - c[2];
          if (arr[arr.length - 1] > val) val = arr[arr.length - 1];
          val = round6(val);
          arr.push(val);
          if (val >= (origins ? origins[lastIdx] || 0 : 0)) lastIdx = arr.length - 1;
          return arr;
        }, null);
        if (absLen === 0) lastIdx = 0;
        origins.push(round6(totalLen));
      }

      if (!slideCount) {
        slider.details = null;
        return;
      }

      // 计算范围
      const loop = opts.loop;
      const range = opts.range;
      loopMin = minBound = loop ? getProp(loop, 'min', -Infinity) : 0;
      loopMax = maxBound = loop ? getProp(loop, 'max', Infinity) : lastIdx;

      const rMin = getProp(range, 'min', null);
      const rMax = getProp(range, 'max', null);
      if (rMin !== null) minBound = rMin;
      if (rMax !== null) maxBound = rMax;

      posMin = minBound === -Infinity ? minBound : idxToDist(minBound || 0, true, 0);
      posMax = maxBound === Infinity ? maxBound : idxToDist(maxBound, true, 0);

      if (rMax === null) loopMax = maxBound;
      posMin = round6(posMin);
      posMax = round6(posMax);

      lastEmittedAbs = null;
      history = [];

      if (typeof initial === 'number') {
        position = 0;
        const dist = calcDist(clampIdx(initial), 0);
        if (dist) add(dist);
      } else {
        getDetails();
        slider.emit('detailsChanged');
      }
    };

    return {
      get details() { return getDetails(); },
      absToRel, add, distToIdx, idxToDist, init,
      to: (newPos) => { add(newPos - position); },
      velocity,
    };
  };

  // ──────────────────────────────────────────────
  // Snap 动画插件 — 拖拽释放后吸附到最近 slide
  // ──────────────────────────────────────────────

  const snapPlugin = (slider) => {
    let lastAbs, curAbs, wasDragging;
    let absMin, absMax, idxMin, idxMax;

    const easeOutQuint = (t) => 1 + --t * t * t * t * t;

    const updateBounds = () => {
      const d = slider.track.details;
      if (d) {
        absMin = d.min; absMax = d.max;
        idxMin = d.minIdx; idxMax = d.maxIdx;
      }
    };

    const clampFreeIdx = (n) => clamp(n, idxMin, idxMax);

    slider.on('created', updateBounds);
    slider.on('updated', updateBounds);
    slider.on('optionsChanged', updateBounds);

    slider.on('dragStarted', () => {
      wasDragging = false;
      slider.animator.stop();
      lastAbs = curAbs = slider.track.details.abs;
    });

    slider.on('dragChecked', () => { wasDragging = true; });

    slider.on('dragged', () => {
      curAbs = slider.track.details.abs;
    });

    slider.on('dragEnded', () => {
      const v = wasDragging ? slider.track.velocity() : 0;
      const details = slider.track.details;
      const pos = details.position;
      let dir = sign(v);

      if (pos > absMax || pos < absMin) dir = 0;

      let target = lastAbs + dir;
      const targetSlide = details.slides[slider.track.absToRel(target)];
      if (targetSlide && targetSlide.portion === 0) target -= dir;
      if (lastAbs !== curAbs) target = curAbs;

      const testDist = slider.track.idxToDist(target, true);
      if (testDist !== null && sign(testDist) !== dir) target += dir;
      target = clampFreeIdx(target);

      const dist = slider.track.idxToDist(target, true);
      if (dist === null) return;

      slider.animator.start([{
        distance: dist,
        duration: 500,
        easing: easeOutQuint,
      }]);
    });
  };

  // ──────────────────────────────────────────────
  // Drag 插件 — touch/mouse 拖拽
  // ──────────────────────────────────────────────

  const dragPlugin = (slider) => {
    const events = createEventBinder();
    let container, speedFn, size, dragActive, tracking;
    let moved, startPos, dragId, dragDir;

    const getCoord = (e) => {
      const touches = e.targetTouches || [];
      return touches[0] ? touches[0].screenX : e.pageX;
    };

    let prevCoord, prevCross;
    const checkDir = (e) => {
      const touches = e.targetTouches || [];
      const x = touches[0] ? touches[0].screenX : e.pageX;
      const y = touches[0] ? touches[0].screenY : e.pageY;
      const isHorizontal = prevCoord !== undefined && prevCross !== undefined &&
        Math.abs(prevCross - y) <= Math.abs(prevCoord - x);
      prevCoord = x;
      prevCross = y;
      return isHorizontal;
    };

    const prevent = (e) => { if (e.cancelable && !e.defaultPrevented) e.preventDefault(); };
    const stopProp = (e) => { if (e.stopPropagation) e.stopPropagation(); };

    const boundDrag = (raw) => {
      const details = slider.track.details;
      if (!details) return 0;
      const pos = details.position;
      return clamp(raw, details.min - pos, details.max - pos);
    };

    const onDragStart = (e) => {
      if (dragActive || !slider.track.details || !slider.track.details.slides.length) return;
      moved = 0;
      dragActive = true;
      tracking = true;
      prevCoord = undefined;
      prevCross = undefined;

      const touches = e.targetTouches || [];
      dragId = touches[0] ? touches[0].identifier : 'mouse';
      startPos = getCoord(e);
      slider.emit('dragStarted');
    };

    const onDrag = (e) => {
      if (!dragActive) return;
      // ID 校验（touch 场景）
      const touches = e.targetTouches || [];
      const currentId = touches[0] ? touches[0].identifier : 'mouse';
      if (currentId !== dragId) return;

      const pos = getCoord(e);

      if (tracking) {
        if (!checkDir(e)) return onDragEnd(e);
        startPos = pos;
        tracking = false;
        slider.emit('dragChecked');
      }

      prevent(e);
      const rawDelta = speedFn(startPos - pos) / size * dragDir;
      const bounded = boundDrag(rawDelta);
      dragDir = sign(bounded) || dragDir;

      moved += bounded;
      if (!moved && Math.abs(moved * size) > 5) moved = true;

      slider.track.add(bounded);
      startPos = pos;
      slider.emit('dragged');
    };

    const onDragEnd = (e) => {
      if (!dragActive) return;
      // changedTouches ID 校验
      const changed = e.changedTouches || [];
      const endId = changed[0] ? changed[0].identifier : 'mouse';
      if (endId !== dragId) return;
      dragActive = false;
      slider.emit('dragEnded');
    };

    const clickGuard = (e) => {
      if (moved) { stopProp(e); prevent(e); }
    };

    const setup = () => {
      events.purge();
      if (!slider.options.drag || slider.options.disabled) return;

      speedFn = (v) => v;
      dragDir = 1;
      size = slider.size;
      container = slider.container;

      events.add(container, 'dragstart', prevent);
      events.add(container, 'click', clickGuard, { capture: true });

      // Mouse
      events.add(container, 'mousedown', onDragStart);
      events.add(container, 'mousemove', onDrag);
      events.add(container, 'mouseleave', onDragEnd);
      events.add(container, 'mouseup', onDragEnd);

      // Touch
      events.add(container, 'touchstart', onDragStart, { passive: true });
      events.add(container, 'touchmove', onDrag, { passive: false });
      events.add(container, 'touchend', onDragEnd);
      events.add(container, 'touchcancel', onDragEnd);

      // 阻止拖拽时页面滚动
      events.add(window, 'wheel', (e) => { if (dragActive) prevent(e); }, { passive: false });
    };

    const updateSize = () => { size = slider.size; };

    slider.on('created', setup);
    slider.on('optionsChanged', setup);
    slider.on('updated', updateSize);
    slider.on('destroyed', () => events.purge());
  };

  // ──────────────────────────────────────────────
  // Render 插件 — 计算并设置 slide 位置
  // ──────────────────────────────────────────────

  const renderPlugin = (slider) => {
    let slides = null;
    let autoSize = false;

    const roundPx = (v) => Math.round(v);

    const setSize = (el, val) => {
      const px = val !== null ? roundPx(val) + 'px' : null;
      el.style.minWidth = px;
      el.style.maxWidth = px;
    };

    const setPos = (el, val) => {
      el.style.transform = val !== null
        ? `translate3d(${roundPx(val)}px, 0, 0)`
        : null;
    };

    const render = () => {
      const details = slider.track.details;
      if (!details || !slides) return;
      const containerSize = slider.size;
      let offset = 0;

      slides.forEach((el, i) => {
        const slideData = details.slides[i];
        if (!slideData) return;
        const slideSize = slideData.size * containerSize;
        if (!autoSize) setSize(el, slideSize);
        setPos(el, slideData.distance * containerSize - offset);
        offset += slideSize;
      });
    };

    const teardown = () => {
      if (slides) {
        slides.forEach((el) => {
          setSize(el, null);
          setPos(el, null);
        });
        slides = null;
      }
      slider.on('detailsChanged', render, true);
    };

    const init = () => {
      teardown();
      if (slider.options.disabled) return;
      autoSize = getProp(slider.options.slides, 'perView', null) === 'auto';
      slider.on('detailsChanged', render);
      slides = slider.slides;
      if (slides.length) render();
    };

    slider.on('created', init);
    slider.on('optionsChanged', init);
    slider.on('beforeOptionsChanged', teardown);
    slider.on('updated', () => { if (slides) render(); });
    slider.on('destroyed', teardown);
  };

  // ──────────────────────────────────────────────
  // Container 插件 — 选项解析 + 响应式 + resize
  // ──────────────────────────────────────────────

  const containerPlugin = (selector, defaults) => (slider) => {
    const events = createEventBinder();
    let currentBP = false;
    let baseOpts = {};
    let breakpointMedias = [];
    let lastSize = 0;

    const resolveTrackConfig = () => {
      const slidesOpt = slider.options.slides;

      if (typeof slidesOpt === 'function') {
        slider.options.trackConfig = slidesOpt(slider.size, slider.slides);
        return;
      }

      const els = slider.slides;
      const count = typeof slidesOpt === 'number' ? slidesOpt : getProp(slidesOpt, 'number', els.length, true);
      const perView = getProp(slidesOpt, 'perView', 1, true);
      const spacing = (getProp(slidesOpt, 'spacing', 0, true) / slider.size) || 0;
      const gap = perView === 'auto' ? spacing : spacing / perView;
      const origin = getProp(slidesOpt, 'origin', 'auto');

      const config = [];
      let total = 0;

      for (let i = 0; i < count; i++) {
        const slideEl = els[i];
        const s = perView === 'auto'
          ? (getSize(slideEl).width / slider.size || 1)
          : (1 / perView - spacing + gap);
        const o = origin === 'center' ? 0.5 - s / 2 : origin === 'auto' ? 0 : origin;
        config.push({ origin: o, size: s, spacing });
        total += s;
      }

      total += spacing * (count - 1);

      // auto origin 尾部对齐
      if (origin === 'auto' && !slider.options.loop && perView !== 1) {
        let acc = 0;
        config.forEach((c) => {
          const remaining = total - acc;
          acc += c.size + spacing;
          if (remaining < 1) c.origin = 1 - remaining - (total > 1 ? 0 : 1 - total);
        });
      }

      slider.options.trackConfig = config;
    };

    const updateSize = () => {
      const s = getSize(slider.container);
      slider.size = s.width || 1;
    };

    const resolveSlides = () => {
      slider.slides = queryElements(slider.options.selector, slider.container);
    };

    const resetTrack = (idx) => {
      slider.animator.stop();
      const d = slider.track.details;
      slider.track.init(idx != null ? idx : d ? d.abs : 0);
    };

    const matchBreakpoint = () => {
      let matched = null;
      breakpointMedias.forEach((mq) => { if (mq.matches) matched = mq._bp; });
      if (matched === currentBP) return false;
      if (currentBP !== false) slider.emit('beforeOptionsChanged');
      currentBP = matched;
      const overrides = matched ? baseOpts.breakpoints[matched] : {};
      slider.options = { ...baseOpts, ...overrides };
      updateSize();
      resolveSlides();
      resolveTrackConfig();
      return true;
    };

    const applyOptions = (idx) => {
      resetTrack(idx);
      slider.emit('optionsChanged');
    };

    const setOptions = (opts) => {
      currentBP = false;
      baseOpts = { ...defaults, ...opts };
      events.purge();
      lastSize = slider.size;
      breakpointMedias = [];

      for (const bp in baseOpts.breakpoints || {}) {
        const mq = window.matchMedia(bp);
        mq._bp = bp;
        breakpointMedias.push(mq);
        events.add(mq, 'change', () => { if (matchBreakpoint()) applyOptions(); });
      }

      events.add(window, 'resize', onResize);
      matchBreakpoint();
    };

    const onResize = () => {
      updateSize();
      if (slider.options.disabled || slider.size === lastSize) return;
      lastSize = slider.size;
      resolveSlides();
      resolveTrackConfig();
      resetTrack();
      slider.emit('updated');
    };

    // 初始化
    const els = queryElements(selector);
    slider.container = els.length ? els[0] : null;

    slider.destroy = () => {
      events.purge();
      slider.emit('destroyed');
    };

    slider.prev = () => {
      const d = slider.track.details;
      if (d) slider.moveToIdx(d.abs - 1, true);
    };

    slider.next = () => {
      const d = slider.track.details;
      if (d) slider.moveToIdx(d.abs + 1, true);
    };

    slider.update = (idx) => {
      updateSize();
      resolveSlides();
      resolveTrackConfig();
      resetTrack(idx);
      slider.emit('updated');
    };

    setOptions(slider.options);
  };

  // ──────────────────────────────────────────────
  // Core — 组装引擎
  // ──────────────────────────────────────────────

  const createSlider = (container, options = {}, plugins = []) => {
    try {
      const eventHandlers = {};
      const easeOutQuint = (t) => 1 + --t * t * t * t * t;

      const slider = {
        container: null,
        slides: [],
        options,
        size: 0,
        track: null,
        animator: null,

        emit(name) {
          const handlers = eventHandlers[name];
          if (handlers) handlers.forEach((fn) => fn(slider));
          const optHandler = slider.options && slider.options[name];
          if (optHandler) optHandler(slider);
        },

        on(name, fn, remove = false) {
          if (!eventHandlers[name]) eventHandlers[name] = [];
          const idx = eventHandlers[name].indexOf(fn);
          if (idx > -1) {
            if (remove) eventHandlers[name].splice(idx, 1);
          } else if (!remove) {
            eventHandlers[name].push(fn);
          }
        },

        moveToIdx(idx, closest = false, animation) {
          const dist = slider.track.idxToDist(idx, closest);
          if (dist == null) return;
          const defaults = slider.options.defaultAnimation;
          slider.animator.start([{
            distance: dist,
            duration: getProp(animation || defaults, 'duration', 500),
            easing: getProp(animation || defaults, 'easing', easeOutQuint),
          }]);
        },
      };

      // 组装核心
      slider.track = createTrack(slider);
      slider.animator = createAnimator(slider);

      // 组装插件
      const allPlugins = [
        containerPlugin(container, {
          drag: true,
          selector: '.ai-slide',
        }),
        renderPlugin,
        dragPlugin,
        snapPlugin,
        ...plugins,
      ];

      allPlugins.forEach((p) => p(slider));

      // 启动
      slider.track.init(slider.options.initial || 0);
      slider.emit('created');

      return slider;
    } catch (err) {
      console.error('[AiAnimation]', err);
    }
  };

  // ──────────────────────────────────────────────
  // <ai-slider> Web Component — 声明式 slider
  // ──────────────────────────────────────────────
  //
  // 用法：
  //   <ai-slider per-view="1" spacing="0" loop drag>
  //     <div class="ai-slide">...</div>
  //     <div class="ai-slide">...</div>
  //   </ai-slider>
  //
  // 属性（全部可选，均有默认值）：
  //   per-view   : number | "auto"   — 每屏显示几张（默认 1）
  //   spacing    : number (px)       — slide 间距（默认 0）
  //   loop       : boolean           — 循环模式（默认 false）
  //   drag       : boolean           — 启用拖拽（默认 true）
  //   initial    : number            — 初始 slide 索引（默认 0）
  //   selector   : string            — slide 选择器（默认 ".ai-slide"）
  //   duration   : number (ms)       — 默认动画时长（默认 500）
  //   disabled   : boolean           — 禁用 slider
  //
  // JS API：
  //   el.instance     — 底层 slider 实例（含 track/animator 等）
  //   el.moveToIdx(i) — 跳转到指定 slide
  //   el.next()       — 下一张
  //   el.prev()       — 上一张
  //   el.refresh()    — 重新初始化（DOM 变化后调用）
  //   el.destroy()    — 销毁
  //
  // 事件（在元素上监听）：
  //   slider:created       — 初始化完成
  //   slider:slideChanged  — slide 切换（detail: { abs, rel }）
  //   slider:destroyed     — 销毁
  //

  if (!customElements.get('ai-slider')) {
    customElements.define('ai-slider', class AiSlider extends HTMLElement {

      connectedCallback() {
        // 延迟一帧，确保子元素（slides）已渲染
        requestAnimationFrame(() => this._init());
      }

      disconnectedCallback() {
        this._destroy();
      }

      // ── 属性解析 ──

      _parseOptions() {
        const perView = this.getAttribute('per-view');
        const spacing = this.getAttribute('spacing');
        const initial = this.getAttribute('initial');
        const duration = this.getAttribute('duration');
        const selector = this.getAttribute('selector') || '.ai-slide';

        const opts = {
          selector,
          drag: !this.hasAttribute('disabled') && this.getAttribute('drag') !== 'false',
          disabled: this.hasAttribute('disabled'),
          initial: initial ? parseInt(initial, 10) : 0,
          defaultAnimation: { duration: duration ? parseInt(duration, 10) : 500 },
          slides: {
            perView: perView === 'auto' ? 'auto' : (perView ? parseFloat(perView) : 1),
            spacing: spacing ? parseFloat(spacing) : 0,
          },
        };

        if (this.hasAttribute('loop')) {
          opts.loop = this.getAttribute('loop') !== 'false';
        } else {
          opts.loop = false;
        }

        // 响应式断点（通过 data-breakpoints JSON）
        const bpAttr = this.getAttribute('data-breakpoints');
        if (bpAttr) {
          try { opts.breakpoints = JSON.parse(bpAttr); } catch (e) { /* ignore */ }
        }

        return opts;
      }

      // ── 生命周期 ──

      _init() {
        if (this._slider) return;

        const opts = this._parseOptions();

        // 注入事件钩子 → 派发 DOM 事件
        opts.created = () => {
          this.dispatchEvent(new CustomEvent('slider:created', { bubbles: true }));
        };
        opts.slideChanged = (s) => {
          const d = s.track.details;
          this.setAttribute('data-current', d.rel);
          this.dispatchEvent(new CustomEvent('slider:slideChanged', {
            bubbles: true,
            detail: { abs: d.abs, rel: d.rel, progress: d.progress },
          }));
        };
        opts.destroyed = () => {
          this.dispatchEvent(new CustomEvent('slider:destroyed', { bubbles: true }));
        };

        this._slider = createSlider(this, opts);
      }

      _destroy() {
        if (this._slider) {
          this._slider.destroy();
          this._slider = null;
        }
      }

      // ── 公开 API ──

      /** 底层 slider 实例 */
      get instance() { return this._slider; }

      /** 跳转到指定 slide */
      moveToIdx(idx, closest = false, animation) {
        if (this._slider) this._slider.moveToIdx(idx, closest, animation);
      }

      /** 下一张 */
      next() { if (this._slider) this._slider.next(); }

      /** 上一张 */
      prev() { if (this._slider) this._slider.prev(); }

      /** 重新初始化（slide 数量变化后调用） */
      refresh(idx) {
        if (this._slider) this._slider.update(idx);
      }

      /** 销毁 */
      destroy() { this._destroy(); }

      /** 当前 slide 索引 */
      get currentIndex() {
        if (!this._slider) return 0;
        const d = this._slider.track.details;
        return d ? d.rel : 0;
      }

      /** slide 总数 */
      get slideCount() {
        return this._slider ? this._slider.slides.length : 0;
      }
    });
  }

})();
